import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft,
  Briefcase,
  Send,
  MapPin,
  Calendar,
  GraduationCap,
  Clock,
  CheckCircle2,
  Loader2,
  Share2,
} from 'lucide-react';
import { jobsApi, POST_TYPE_LABELS, EMPLOYMENT_LABELS, buildJobPostingJsonLd } from './careersApi';
import { shareToSocial } from '../../lib/socialShare';

const SectionList = ({ title, items, icon: Icon, color }) => {
  if (!items?.length) return null;
  return (
    <div className="mb-6">
      <h3 className={`text-lg font-semibold flex items-center gap-2 mb-3 ${color}`}>
        <Icon className="w-5 h-5" />
        {title}
      </h3>
      <ul className="space-y-2">
        {items.map((item, i) => (
          <li key={i} className="flex gap-2 text-sm text-[#5D4E37] leading-relaxed">
            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-600" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const JobDetailPage = ({ slug, onBack, onApply }) => {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    jobsApi
      .getBySlug(slug)
      .then((data) => {
        if (!cancelled) setJob(data);
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // SEO: title, description, canonical, JSON-LD JobPosting
  useEffect(() => {
    if (!job) return undefined;
    const prevTitle = document.title;
    document.title = `${job.title} · Recrutement Pharmacie SADA`;
    const canonicalUrl = `https://phciesada.org/recrutement/${job.slug}`;

    let m = document.querySelector('meta[name="description"]');
    const prevMetaContent = m ? m.getAttribute('content') : null;
    if (!m) {
      m = document.createElement('meta');
      m.name = 'description';
      document.head.appendChild(m);
    }
    m.setAttribute('content', job.description_short?.slice(0, 160) || 'Poste ouvert à la Pharmacie SADA.');

    let canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute('href') : null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    let robotsMeta = document.querySelector('meta[name="robots"]');
    const prevRobots = robotsMeta ? robotsMeta.getAttribute('content') : null;
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.setAttribute('name', 'robots');
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute('content', 'index, follow');

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.jobLd = job.slug;
    script.textContent = JSON.stringify(buildJobPostingJsonLd(job, canonicalUrl));
    document.head.appendChild(script);

    return () => {
      document.title = prevTitle;
      if (m && prevMetaContent !== null) m.setAttribute('content', prevMetaContent);
      if (canonical && prevCanonical !== null) canonical.setAttribute('href', prevCanonical);
      if (robotsMeta && prevRobots !== null) robotsMeta.setAttribute('content', prevRobots);
      script.parentNode?.removeChild(script);
    };
  }, [job]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF6F1]">
        <Loader2 className="w-8 h-8 animate-spin text-[#5D4E37]" />
      </div>
    );
  }

  if (notFound || !job) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAF6F1] p-4">
        <h2 className="text-2xl font-serif font-bold text-[#5D4E37] mb-2">Offre introuvable</h2>
        <p className="text-[#78716C] mb-6">Cette offre n'existe plus ou n'est pas encore publiée.</p>
        <Button onClick={onBack} className="bg-[#5D4E37] hover:bg-[#4A3E2C] text-white rounded-full px-6">
          <ArrowLeft className="w-4 h-4 mr-2" /> Voir les autres postes
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAF6F1] via-white to-[#FAF6F1]" data-testid="page-job-detail">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[#E8DDD4] shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button
            onClick={onBack}
            variant="ghost"
            size="sm"
            className="text-[#5D4E37] hover:bg-[#F5EDE4]"
            data-testid="job-detail-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour
          </Button>
          <h1 className="font-bold text-[#5D4E37] text-sm sm:text-base truncate">
            {job.title}
          </h1>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#5D4E37]/5 via-transparent to-[#C9A086]/10 pointer-events-none" />
        <div className="max-w-5xl mx-auto px-4 pt-10 pb-6 sm:pt-12 relative">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex flex-wrap gap-2 mb-4">
              <Badge className="bg-[#5D4E37] text-white hover:bg-[#4A3E2C]">
                <Briefcase className="w-3 h-3 mr-1" />
                {EMPLOYMENT_LABELS[job.employment_type] || job.employment_type}
              </Badge>
              <Badge variant="outline" className="border-[#C9A086] text-[#5D4E37]">
                {POST_TYPE_LABELS[job.post_type] || job.post_type}
              </Badge>
              <Badge variant="outline" className="border-[#C9A086] text-[#5D4E37]">
                <MapPin className="w-3 h-3 mr-1" />
                {job.city || 'Abidjan'}
              </Badge>
              {job.experience_years > 0 && (
                <Badge variant="outline" className="border-[#C9A086] text-[#5D4E37]">
                  <Clock className="w-3 h-3 mr-1" />
                  {job.experience_years} an(s) exp.
                </Badge>
              )}
              {job.deadline_iso && (
                <Badge variant="outline" className="border-red-300 text-red-700">
                  <Calendar className="w-3 h-3 mr-1" />
                  Jusqu'au {(job.deadline_iso || '').slice(0, 10)}
                </Badge>
              )}
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-[#5D4E37] leading-tight">
              {job.title}
            </h2>
            <p className="mt-4 text-base sm:text-lg text-[#78716C] leading-relaxed max-w-3xl whitespace-pre-line">
              {job.description_short}
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              <Button
                onClick={() => onApply?.(job.slug, job.post_type)}
                className="bg-[#5D4E37] hover:bg-[#4A3E2C] text-white rounded-full px-6 h-11"
                data-testid="job-detail-apply-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                Postuler à ce poste
              </Button>
            </div>
            {(() => {
              const origin = typeof window !== 'undefined' ? window.location.origin : 'https://phciesada.org';
              const shareUrl = `${origin}/recrutement/${job.slug}`;
              const ogUrl = `${origin}/api/careers/jobs/${job.slug}/og?v=${Date.now()}`;
              const doShare = (ch) => shareToSocial(ch, {
                url: shareUrl,
                ogUrl,
                title: job.title,
                subject: `${job.title} — Pharmacie SADA recrute`,
              });
              return (
                <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="job-share-bar">
                  <span className="text-sm text-[#78716C] flex items-center gap-1">
                    <Share2 className="w-4 h-4" /> Partager :
                  </span>
                  {typeof navigator !== 'undefined' && navigator.share && (
                    <Button variant="outline" size="sm" onClick={() => doShare('native')} className="text-[#0EA5E9] border-sky-200 rounded-full" data-testid="job-share-native">
                      <Share2 className="w-4 h-4 mr-1" /> Partager
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => doShare('whatsapp')} className="text-green-600 border-green-200 rounded-full" data-testid="job-share-whatsapp">
                    WhatsApp
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => doShare('facebook')} className="text-blue-600 border-blue-200 rounded-full" data-testid="job-share-facebook">
                    Facebook
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => doShare('instagram')} className="text-pink-600 border-pink-200 hover:bg-pink-50 rounded-full" data-testid="job-share-instagram">
                    Instagram
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => doShare('tiktok')} className="text-neutral-900 border-neutral-300 hover:bg-neutral-100 rounded-full" data-testid="job-share-tiktok">
                    TikTok
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => doShare('linkedin')} className="text-[#0A66C2] border-[#0A66C2]/30 hover:bg-[#0A66C2]/10 rounded-full" data-testid="job-share-linkedin">
                    LinkedIn
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => doShare('twitter')} className="rounded-full" data-testid="job-share-twitter">
                    Twitter
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => doShare('copy')} className="rounded-full" data-testid="job-share-copy">
                    Copier le lien
                  </Button>
                </div>
              );
            })()}
          </motion.div>
        </div>
      </section>

      {/* CONTENU DÉTAILLÉ */}
      <section className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        <Card className="border-[#E8DDD4]">
          <CardContent className="p-6 sm:p-10">
            <SectionList
              title="Missions"
              items={job.missions}
              icon={Briefcase}
              color="text-[#5D4E37]"
            />
            <SectionList
              title="Profil recherché"
              items={job.profile}
              icon={GraduationCap}
              color="text-[#5D4E37]"
            />
            <SectionList
              title="Ce que la Pharmacie SADA offre"
              items={job.offer}
              icon={CheckCircle2}
              color="text-emerald-700"
            />

            <div className="border-t border-[#E8DDD4] mt-8 pt-6 flex flex-wrap gap-3 justify-between items-center">
              <div className="text-sm text-[#78716C]">
                Publié le {(job.published_at || job.created_at || '').slice(0, 10)}
              </div>
              <Button
                onClick={() => onApply?.(job.slug, job.post_type)}
                className="bg-[#5D4E37] hover:bg-[#4A3E2C] text-white rounded-full px-6"
                data-testid="job-detail-apply-btn-bottom"
              >
                <Send className="w-4 h-4 mr-2" />
                Postuler à ce poste
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  );
};

export default JobDetailPage;
